import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock3,
  Sparkles,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

const SECTION_BG = "var(--user-bg, #E8D9D5)";
const CARD_BG = "#FBF8F4";
const CARD_TEXT = "#2B1718";
const TEXT_SOFT = "var(--user-text-soft, #786B68)";
const GOLD = "var(--user-gold, #D6B33F)";
const MAROON = "var(--user-maroon, #2B0000)";

export default function BlogsPage() {
  const pageRef = useRef(null);
  const cardRefs = useRef([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getBlogs.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted) return;

        if (response?.success && Array.isArray(response.data)) {
          setBlogs(response.data);
        } else {
          setBlogs([]);
        }
      })
      .catch((error) => {
        console.error("Unable to load blogs:", error);

        if (mounted) {
          setBlogs([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const page = pageRef.current;
    const cards = cardRefs.current.filter(Boolean);

    if (!page || cards.length === 0) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion) {
      gsap.set(cards, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        scale: 1,
      });

      return undefined;
    }

    const ctx = gsap.context(() => {
      cards.forEach((card, index) => {
        gsap.fromTo(
          card,
          {
            autoAlpha: 0,
            y: 45,
            scale: 0.97,
          },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 0.72,
            delay: (index % 3) * 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 90%",
              once: true,
              invalidateOnRefresh: true,
            },
          }
        );
      });

      window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 200);
    }, page);

    return () => ctx.revert();
  }, [blogs.length]);

  const formatDate = (blog) => {
    if (blog.date) return blog.date;
    if (!blog.created_at) return "";

    const date = new Date(blog.created_at);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <main
      ref={pageRef}
      className="min-h-screen"
      style={{
        backgroundColor: SECTION_BG,
      }}
    >
      {/* PAGE HERO */}
      <section className="relative overflow-hidden px-5 pb-10 pt-10 sm:px-8 lg:px-14 lg:pb-14 lg:pt-14">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(214,179,63,0.16), transparent 34%)",
          }}
        />

        <div className="relative mx-auto max-w-[1450px]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] transition hover:gap-3"
            style={{ color: MAROON }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-3">
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
              <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
            </div>

            <p
              className="mt-5 text-[10px] font-bold uppercase tracking-[0.32em] sm:text-xs"
              style={{ color: GOLD }}
            >
              SL Gold Stories
            </p>

            <h1
              className="mt-3 font-serif text-[38px] font-semibold leading-tight sm:text-[50px] lg:text-[62px]"
              style={{ color: MAROON }}
            >
              Our Blogs
            </h1>

            <p
              className="mx-auto mt-4 max-w-2xl text-sm leading-7 sm:text-base"
              style={{ color: TEXT_SOFT }}
            >
              Stories, jewellery knowledge, care guides and craftsmanship from
              SL Gold.
            </p>
          </div>
        </div>
      </section>

      {/* BLOG GRID */}
      <section className="px-3 pb-16 sm:px-8 lg:px-14 lg:pb-24">
        <div className="mx-auto max-w-[1450px]">
          {loading ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="h-[430px] animate-pulse rounded-[22px] bg-white/30"
                />
              ))}
            </div>
          ) : blogs.length === 0 ? (
            <div
              className="rounded-[24px] border px-6 py-14 text-center"
              style={{
                borderColor: "rgba(43,0,0,0.10)",
                backgroundColor: CARD_BG,
              }}
            >
              <h2
                className="font-serif text-2xl font-semibold"
                style={{ color: CARD_TEXT }}
              >
                No blogs available
              </h2>

              <p className="mt-2 text-sm" style={{ color: TEXT_SOFT }}>
                Published blogs will appear here.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-7">
              {blogs.map((blog, index) => (
                <article
                  key={blog.id || blog.title}
                  ref={(element) => {
                    cardRefs.current[index] = element;
                  }}
                  className="
                    group
                    relative
                    flex
                    min-h-[182px]
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-black/[0.06]
                    shadow-[0_10px_30px_rgba(48,22,20,0.09)]
                    will-change-transform

                    md:min-h-[455px]
                    md:flex-col
                    md:rounded-[22px]
                    md:shadow-[0_12px_35px_rgba(48,22,20,0.09)]
                  "
                  style={{
                    backgroundColor: CARD_BG,
                  }}
                >
                  {/* IMAGE */}
                  <div
                    className="
                      relative
                      min-h-[182px]
                      w-[41%]
                      shrink-0
                      overflow-hidden

                      md:h-[220px]
                      md:min-h-0
                      md:w-full
                    "
                  >
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      loading="lazy"
                      decoding="async"
                      className="
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-700
                        ease-out
                        group-hover:scale-[1.04]
                      "
                    />

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-black/[0.03]" />
                    <div className="pointer-events-none absolute inset-[5px] rounded-[13px] border border-white/25 md:hidden" />
                  </div>

                  {/* CONTENT */}
                  <div
                    className="
                      relative
                      flex
                      min-w-0
                      flex-1
                      flex-col
                      px-3
                      py-3

                      sm:px-4
                      sm:py-4

                      md:px-6
                      md:pb-6
                      md:pt-5
                    "
                  >
                    <div
                      className="
                        mt-2
                        flex
                        flex-wrap
                        items-center
                        gap-x-2
                        gap-y-1
                        text-[8px]
                        font-medium

                        sm:text-[9px]

                        md:mt-0
                        md:border-b
                        md:pb-4
                        md:text-[14px]
                      "
                      style={{
                        color: TEXT_SOFT,
                        borderColor: "rgba(43,23,24,0.09)",
                      }}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays
                          size={10}
                          strokeWidth={1.8}
                          className="md:h-[17px] md:w-[17px]"
                        />
                        <span className="whitespace-nowrap">
                          {formatDate(blog)}
                        </span>
                      </span>

                      <span
                        className="h-[3px] w-[3px] rounded-full md:hidden"
                        style={{
                          backgroundColor: "rgba(120,107,104,0.45)",
                        }}
                      />

                      <span className="inline-flex items-center gap-1.5">
                        <Clock3
                          size={10}
                          strokeWidth={1.8}
                          className="md:h-[17px] md:w-[17px]"
                        />
                        <span className="whitespace-nowrap">
                          {blog.read_time}
                        </span>
                      </span>
                    </div>

                    <h2
                      className="
                        mt-2
                        line-clamp-2
                        font-serif
                        text-[14px]
                        font-semibold
                        leading-[1.12]
                        tracking-[-0.02em]

                        sm:text-[16px]

                        md:mt-5
                        md:text-[27px]
                        md:leading-[1.16]

                        lg:text-[30px]
                      "
                      style={{
                        color: CARD_TEXT,
                      }}
                    >
                      {blog.title}
                    </h2>

                    <p
                      className="
                        mt-1.5
                        line-clamp-2
                        text-[9px]
                        leading-[1.45]

                        sm:text-[10px]

                        md:mt-3
                        md:line-clamp-3
                        md:text-[15px]
                        md:leading-[1.65]

                        lg:text-[16px]
                      "
                      style={{
                        color: TEXT_SOFT,
                      }}
                    >
                      {blog.short_description}
                    </p>

                    <div className="mt-auto pt-2 md:pt-5">
                      <Link
                        to={`/blog/${blog.id}`}
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          text-[10px]
                          font-semibold
                          transition-[gap,color]
                          duration-300

                          hover:gap-2.5

                          md:gap-2
                          md:text-[15px]
                          md:hover:gap-3

                          lg:text-[16px]
                        "
                        style={{
                          color: "#B48728",
                        }}
                      >
                        Read More

                        <ArrowRight
                          size={12}
                          strokeWidth={2}
                          className="md:h-[18px] md:w-[18px]"
                        />
                      </Link>
                    </div>
                  </div>

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-x-7
                      bottom-0
                      hidden
                      h-[2px]
                      origin-center
                      scale-x-0
                      transition-transform
                      duration-500

                      group-hover:scale-x-100

                      md:block
                    "
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, #D6B33F, transparent)",
                    }}
                  />
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}